import { useCallback, useState } from 'react';
import { useFig } from '../../session/FigProvider.js';
import type { Client, ClientPortalUser } from '../../core/api/types.js';
import { EmptyState, ErrorState, RowSkeleton } from '../../components/States.js';
import { Dialog, Field } from '../../components/Dialog.js';
import { useResource } from './useResource.js';

/**
 * The people at each end-client who can sign in to the Client Portal.
 *
 * Without this screen an agency cannot invite anybody, which means the entire Client Portal has
 * no users — the surface exists and nobody can reach it.
 *
 * **Two independent off switches, and the copy has to keep them apart.** A person's own
 * `disabled`, and the whole client's `portalAccessDisabled` (set on the Clients screen). They
 * are deliberately separate so that re-opening a client does not silently restore somebody who
 * was turned off individually. An admin who assumes one undoes the other will hand access back
 * to a person they meant to remove, so the row says which switch is closed rather than showing
 * a single ambiguous "no access".
 */
export function PortalUsers() {
  const { client: api } = useFig();
  const loadClients = useCallback(() => api.listClients({ sortBy: 'Name', sortDirection: 'Asc' }), [api]);
  const clients = useResource<readonly Client[]>(loadClients);
  const [clientId, setClientId] = useState<string>('');

  const loadUsers = useCallback(
    () => api.listPortalUsers(clientId || undefined), [api, clientId]);
  const users = useResource<readonly ClientPortalUser[]>(loadUsers);
  const [inviting, setInviting] = useState(false);

  if (clients.error !== null) return <ErrorState error={clients.error} onRetry={() => void clients.reload()} />;
  if (users.error !== null) return <ErrorState error={users.error} onRetry={() => void users.reload()} />;

  const list = clients.data ?? [];
  const nameOf = (id: string) => list.find((c) => c.id === id)?.name ?? 'Unknown client';

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">Portal users</h1>
        <button className="btn btn-primary btn-inline" disabled={list.length === 0}
          onClick={() => setInviting(true)}>Invite someone</button>
      </div>
      <p className="state-body" style={{ marginInline: 0 }}>
        These are your clients’ own people. They see only their own organisation’s calls, and
        nothing about your agency.
      </p>

      <div className="field">
        <label className="field-label" htmlFor="pu-client">Show</label>
        <select id="pu-client" className="input input-inline" value={clientId}
          onChange={(e) => setClientId(e.target.value)}>
          <option value="">Everyone, all clients</option>
          {list.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {users.data === null && users.busy && <RowSkeleton count={3} />}

      {users.data?.length === 0 && (
        <EmptyState
          title={clientId ? `Nobody at ${nameOf(clientId)} yet` : 'No portal users yet'}
          body="Invite a contact at one of your clients. They’ll get a link — no password to set up — and they’ll see only that client’s calls."
        />
      )}

      {users.data && users.data.length > 0 && (
        <table className="table">
          <caption className="sr-only">Client portal users, {users.data.length}</caption>
          <thead>
            <tr><th scope="col">Name</th><th scope="col">Email</th><th scope="col">Client</th>
              <th scope="col">Status</th><th scope="col"><span className="sr-only">Actions</span></th></tr>
          </thead>
          <tbody>
            {users.data.map((u) => (
              <PortalUserRow key={u.id} row={u} clientName={nameOf(u.clientId)}
                onChanged={() => void users.reload()} />
            ))}
          </tbody>
        </table>
      )}

      <InviteDialog open={inviting} clients={list} onClose={() => setInviting(false)}
        onInvited={() => { setInviting(false); void users.reload(); }} />
    </>
  );
}

function PortalUserRow({ row, clientName, onChanged }: {
  row: ClientPortalUser; clientName: string; onChanged: () => void;
}) {
  const { client: api } = useFig();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true); setError(null);
    try { await fn(); onChanged(); }
    catch (e) { setError(e instanceof Error ? e.message : 'That did not work.'); }
    finally { setBusy(false); }
  }

  // The two switches are reported separately on purpose - see the note on the component above.
  const status = row.disabled ? 'Turned off'
    : row.clientPortalAccessDisabled ? 'Client’s portal closed'
    : row.pending ? 'Invited' : 'Active';

  return (
    <tr>
      <th scope="row" className="cell-name">{row.name}</th>
      <td>{row.email}</td>
      <td>{clientName}</td>
      <td>
        <span className={`pill pill-${row.disabled || row.clientPortalAccessDisabled ? 'disabled' : row.pending ? 'invited' : 'active'}`}>
          {status}
        </span>
        {row.clientPortalAccessDisabled && !row.disabled && (
          <p className="hint">Re-opening the client on the Clients screen restores this person.</p>
        )}
      </td>
      <td className="cell-actions">
        {row.pending ? (
          // DELETE revokes a PENDING invitation only; once accepted the API returns 409 and
          // the person is disabled instead. Offering an action that will fail is worse than
          // not offering it.
          <button className="btn btn-small" disabled={busy}
            onClick={() => void act(() => api.setPortalUserDisabled(row.id, true))}>
            Turn off
          </button>
        ) : (
          <button className="btn btn-small" disabled={busy}
            onClick={() => void act(() => api.setPortalUserDisabled(row.id, !row.disabled))}>
            {row.disabled ? 'Turn back on' : 'Turn off'}
          </button>
        )}
        {error && <span className="hint hint-error" role="alert">{error}</span>}
      </td>
    </tr>
  );
}

function InviteDialog({ open, clients, onClose, onInvited }: {
  open: boolean; clients: readonly Client[]; onClose: () => void; onInvited: () => void;
}) {
  const { client: api } = useFig();
  const [clientId, setClientId] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.invitePortalUser({ clientId, email: email.trim(), name: name.trim() });
      setEmail(''); setName('');
      onInvited();
    } catch (err) { setError(err instanceof Error ? err.message : 'That did not work.'); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onClose={onClose} title="Invite someone to a client’s portal">
      <form onSubmit={submit} noValidate>
        <div className="field">
          <label className="field-label" htmlFor="pu-invite-client">Which client</label>
          <select id="pu-invite-client" className="input" value={clientId}
            onChange={(e) => setClientId(e.target.value)} required>
            <option value="">Choose a client…</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <Field id="pu-name" label="Their name" value={name} autoComplete="name"
          onChange={(e) => setName(e.target.value)} required />
        <Field id="pu-email" label="Email address" type="email" value={email}
          autoComplete="email" inputMode="email" onChange={(e) => setEmail(e.target.value)} required
          hint="They’ll get a link. More than one person per client is fine." />
        {error && <p className="hint hint-error" role="alert">{error}</p>}
        <div className="dialog-actions">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary btn-inline"
            disabled={busy || !clientId || !email.trim() || !name.trim()}>
            {busy ? 'Sending…' : 'Send invitation'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
